import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { MUTATION_KEY } from './keys';
import { clmApiClient, clmUserApiClient } from '../clients';
import type {
  ApiErrorResponse,
  PatientCreateRequest,
  PatientCreateResponse,
} from '../types';
import { isApiErrorResponse } from '../utils';

const patientCreateMutation = async (
  data: PatientCreateRequest,
): Promise<PatientCreateResponse> => {
  let realAPIResponse;

  // -------------------------------
  // REAL API LOGIC (Temporary Setup)
  // -------------------------------

  // We are wrapping the real API call in a try-catch to ensure that it does not block
  // the mocked API logic in case of failure, as the real backend is not yet stable.
  try {
    realAPIResponse = await clmUserApiClient.post<
      PatientCreateResponse,
      ApiErrorResponse
    >('patient', data, {
      params: { include: 'setting' },
    });

    if (!realAPIResponse.data) {
      throw new Error('No data returned from create patient');
    }

    if (!realAPIResponse.ok) {
      if (isApiErrorResponse(realAPIResponse.data)) {
        throw new Error(realAPIResponse.data.details);
      }
      throw new Error('Unknown error');
    }
  } catch (error) {
    console.error('Error creating patient:', error);
    throw error;
  }

  // -------------------------------
  // MOCKED API LOGIC (Fallback)
  // -------------------------------
  // This is still in place because the real backend cannot yet
  // reliably return existing patient data.
  const mockedResponse = await clmApiClient.post<
    PatientCreateResponse,
    ApiErrorResponse
  >(
    'patient',
    {
      ...data,
      patient: {
        ...data.patient,
        // DON'T REMOVE
        id: realAPIResponse.data.id,
      },
    },
    {
      params: { include: 'setting' },
    },
  );

  if (!mockedResponse.data) {
    throw new Error('No data returned from create patient');
  }

  if (!mockedResponse.ok) {
    if (isApiErrorResponse(mockedResponse.data)) {
      throw new Error(mockedResponse.data.details);
    }
    throw new Error('Unknown error');
  }

  return mockedResponse.data;
};

export const usePatientCreateMutation = (
  config?: Omit<
    UseMutationOptions<PatientCreateResponse, Error, PatientCreateRequest>,
    'mutationKey' | 'mutationFn'
  >,
) =>
  useMutation({
    mutationKey: [MUTATION_KEY.PATIENT_CREATE],
    mutationFn: patientCreateMutation,
    ...config,
  });
